'use client'

import { useUser } from '@auth0/nextjs-auth0'
import getUserMeta from '@/lib/get-user-meta'

import { setCookie, getCookie } from '@/lib/utils/cookies'

import { Dialog, Modal, ModalOverlay } from 'react-aria-components'

import { AccountFormWrapper } from '@/app/account/update-my-details/client/account-form'

import { displayFieldProps } from '@/app/account/update-my-details/client/account-form'
import { useEffect } from 'react'
import { useState } from 'react'

export default function OnboardingBlocker() {
  const { user, isLoading } = useUser()
  const [userMeta, setUserMeta] = useState(null)
  const [profileCompleted, setProfileCompleted] = useState(true)

  const userEmail = user?.email || null

  useEffect(() => {
    if (!userEmail || !user) {
      return
    }

    // Check if the user has dismissed the onboarding blocker
    const isDismissed = getCookie(`onboarding-blocker-dismissed`)

    if (isDismissed === 'true') {
      setProfileCompleted(true)
      return
    }

    const fetchUserData = async () => {
      const userMeta = await getUserMeta(user?.email)
      user.meta = userMeta || {}

      if (!userMeta) {
        setUserMeta(null)
        setProfileCompleted(false)
        return
      }

      const { first_name: firstName, last_name: lastName, country: country } = userMeta

      // Store user meta in state
      setUserMeta(userMeta)

      // Set based on whether the user has all required fields
      const hasRequiredFields = firstName?.length > 0 && lastName?.length > 0 && country?.length > 0
      setProfileCompleted(hasRequiredFields)
    }

    fetchUserData()
  }, [userEmail, user])

  // Once the user successfully updates their account, we want to close the onboarding blocker
  const handleSuccess = () => {
    setProfileCompleted(true)
  }

  const handleDismiss = () => {
    setProfileCompleted(true)
    setCookie(`onboarding-blocker-dismissed`, 'true', 1) // Dismiss for 1 day
  }

  // If the user has completed their profile or is not logged in, don't show onboarding blocker
  if (profileCompleted || !user || isLoading) {
    return null
  }

  // Get required data from the user meta
  const { first_name: firstName, last_name: lastName, country: country } = userMeta

  // Double check that the user has all required data
  if (firstName?.length > 0 && lastName?.length > 0 && country?.length > 0) {
    return null
  }

  const fields = {
    first_name: {},
    last_name: {},
    city: {},
    country: {},
  } as displayFieldProps

  return (
    <ModalOverlay className="onboarding-modal-overlay" isOpen={true} isDismissable={false}>
      <Modal className="max-h-[100vh] w-full overflow-auto">
        <Dialog className="px-[var(--container-padding)]">
          <div className="mx-auto my-s max-w-[720px] overflow-hidden rounded-lg border border-border bg-theme px-xxl py-l text-fg theme--base-0 m:w-full m:px-l">
            <div className="flex flex-col gap-y-l">
              <div className="flex flex-col items-center justify-center gap-y-s text-center">
                <svg aria-hidden="true" className="aspect-[173/185] w-[46px]">
                  <use href={`${process.env.NEXT_PUBLIC_SPRITE_PATH}#icon-logo-simple`}></use>
                </svg>

                <span className="h3 mb-0 font-serif">Complete your profile</span>
                <p>Complete your profile to get the most out of your experience.</p>
              </div>

              <div className="[&_.form-footer]:-mx-xxl [&_.form-footer]:px-xxl m:[&_.form-footer]:-mx-l m:[&_.form-footer]:px-l">
                <div className="flex">
                  <AccountFormWrapper
                    fields={fields}
                    handleSuccess={handleSuccess}
                    footerLeftChildren={
                      <button
                        type="button"
                        className="italic text-grey-700 hover:text-primary"
                        onClick={handleDismiss}
                        aria-label="Dismiss onboarding blocker">
                        I&apos;ll do this later
                      </button>
                    }
                    submitText="Finish"
                  />
                </div>
              </div>
            </div>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  )
}
